/*
 * Copyright (c) 2025. Orion Analytics. All Rights Reserved
 */

import React, { useState,useEffect } from "react";
import Plot from "react-plotly.js";
import Papa from "papaparse";
import {fetchMarketIndices} from "../../api";

const VaRVisualizerDetailed = () => {
  const [returns, setReturns] = useState([]);
  const [confidence, setConfidence] = useState(95);
  const [days, setDays] = useState(1);
  const [method, setMethod] = useState("historical");
  const [symbol, setSymbol] = useState("Nifty50");
  const [meanOverride, setMeanOverride] = useState(0);
  const [stdDevOverride, setStdDevOverride] = useState(1);
  const [lgd, setLgd] = useState(40);
  const [indices, setIndices] = useState([]);


  const excelIconUrl = "https://img.icons8.com/color/48/microsoft-excel-2019--v1.png";
  const highlightPortion = 1; // or 1.0 for full CVaR shading

    useEffect(() => {
      fetchMarketIndices()
    .then(data => {
        console.log("INDEX DATA  ->",data)
        const indices = data
        setIndices(JSON.parse(indices.hist_df));
        })
    }, []);


  // const predefinedReturns = {
  //   Nifty50: [...Array(250)].map(() => (Math.random() - 0.5) * 0.03),
  //   BankNifty: [...Array(250)].map(() => (Math.random() - 0.5) * 0.04),
  // };

  const predefinedReturns = {
    Nifty50: indices
        .filter(item => item.symbol === "NIFTY 50")
        .map(item => item["percent_change"]),
    BankNifty: indices
        .filter(item => item.symbol === "NIFTY BANK")
        .map(item => item["percent_change"]),
  };

  console.log(indices)
  const handleFileUpload = (e) => {
    Papa.parse(e.target.files[0], {
      header: false,
      dynamicTyping: true,
      complete: (result) => {
        const data = result.data.flat().filter(x => typeof x === "number");
        setReturns(data);
      },
    });
  };

  const calculateVaR = () => {
    const data = returns.length ? returns : predefinedReturns[symbol];
    const sorted = [...data].sort((a, b) => a - b);
    const alpha = 1 - confidence / 100;
    const varIndex = Math.floor(sorted.length * alpha);
    const historicalVaR = -sorted[varIndex];

    const mean = meanOverride;
    const stdDev = stdDevOverride;
    const z = -1 * getZScore(confidence);
    const parametricVaR = -(mean * days + z * stdDev * Math.sqrt(days));

    const monteCarloReturns = [...Array(1000)].map(
      () => mean + stdDev * randn_bm()
    );
    const mcSorted = [...monteCarloReturns].sort((a, b) => a - b);
    const monteCarloVaR = -mcSorted[varIndex];

    const selectedVaR =
      method === "historical"
        ? historicalVaR
        : method === "parametric"
        ? parametricVaR
        : monteCarloVaR;

    const cvar =
      method === "historical"
        ? -sorted.slice(0, varIndex).reduce((a, b) => a + b, 0) / varIndex
        : method === "parametric"
        ? -(mean - stdDev * gaussianPDF(z) / alpha)
        : -mcSorted.slice(0, varIndex).reduce((a, b) => a + b, 0) / varIndex;

    const lossDefault = (lgd / 100) * selectedVaR;

    return { data, sorted, selectedVaR, cvar, lossDefault };
  };

  const getZScore = (conf) => {
    const zTable = {
      90: 1.2816,
      95: 1.6449,
      99: 2.3263,
    };
    return zTable[conf] || 1.6449;
  };

  const gaussianPDF = (x) =>
    (1 / Math.sqrt(2 * Math.PI)) * Math.exp(-0.5 * x * x);

  const randn_bm = () => {
    let u = 0,
      v = 0;
    while (u === 0) u = Math.random();
    while (v === 0) v = Math.random();
    return Math.sqrt(-2 * Math.log(u)) * Math.cos(2 * Math.PI * v);
  };

  const { data, sorted, selectedVaR, cvar, lossDefault } = calculateVaR();

  const handleExport = () => {
    const csv = data.map((val) => [val]).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.download = "returns_data.csv";
    link.href = url;
    link.click();
  };

  const cdfY = sorted.map((_, i) => i / sorted.length);

  return (
    <div style={{ fontFamily: "Arial", padding: "20px" }}>
      <h3>📉 VaR Visualizer</h3>

      <div style={{ marginBottom: "20px" }}>
        <label>Method: </label>
        <select value={method} onChange={(e) => setMethod(e.target.value)}>
          <option value="historical">Historical</option>
          <option value="parametric">Parametric</option>
          <option value="montecarlo">Monte Carlo</option>
        </select>

        <label style={{ marginLeft: "20px" }}>Symbol: </label>
        <select value={symbol} onChange={(e) => setSymbol(e.target.value)}>
          <option>Nifty50</option>
          <option>BankNifty</option>
        </select>

        <label style={{ marginLeft: "20px" }}>
          Days:
          <input
            type="number"
            min={1}
            value={days}
            onChange={(e) => setDays(Number(e.target.value))}
          />
        </label>

        <label style={{ marginLeft: "20px" }}>
          Confidence: {confidence}%
          <input
            type="range"
            min={90}
            max={99}
            value={confidence}
            onChange={(e) => setConfidence(Number(e.target.value))}
          />
        </label>

        <label style={{ marginLeft: "20px" }}>
          LGD: {lgd}%
          <input
            type="range"
            min={0}
            max={100}
            value={lgd}
            onChange={(e) => setLgd(Number(e.target.value))}
          />
        </label>

        {method === "parametric" && (
          <>
            <label style={{ marginLeft: "20px" }}>
              Mean:
              <input
                type="number"
                value={meanOverride}
                onChange={(e) => setMeanOverride(Number(e.target.value))}
              />
            </label>
            <label style={{ marginLeft: "10px" }}>
              Std Dev:
              <input
                type="number"
                value={stdDevOverride}
                onChange={(e) => setStdDevOverride(Number(e.target.value))}
              />
            </label>
          </>
        )}

        <input
          type="file"
          accept=".csv"
          onChange={handleFileUpload}
          style={{ marginLeft: "20px" }}
        />
        {/*<button onClick={handleExport} style={{ marginLeft: "10px" }}>*/}
        {/*  Export Returns CSV*/}
        {/*</button>*/}
      </div>
<div style={{ position: "relative", width: "100%" }}>

      <Plot
        data={[
          {
            x: data,
            type: "histogram",
            opacity: 0.6,
            name: "Returns Distribution",
            marker: { color: "#3399cc" },
          },
          {
            x: [-selectedVaR],
            y: [0],
            mode: "markers+text",
            name: `VaR @ ${confidence}%`,
            text: [`VaR: ${(-selectedVaR * 100).toFixed(2)}%`],
            textposition: "top center",
            marker: { color: "red", size: 12 },
          },
          {
            x: [-cvar],
            y: [0],
            mode: "markers+text",
            name: "CVaR",
            text: [`CVaR: ${(-cvar * 100).toFixed(2)}%`],
            textposition: "top center",
            marker: { color: "orange", size: 12 },
          },
          {
            x: sorted,
            y: cdfY,
            name: "CDF",
            yaxis: "y2",
            mode: "lines",
            line: { color: "green", width: 2, dash: "dot" },
          },
        ]}
        layout={{
          title: "VaR & Risk Distribution",
          xaxis: { title: "Returns" },
          yaxis: { title: "Frequency", side: "left" },
          yaxis2: {
            overlaying: "y",
            side: "right",
            title: "CDF",
          },
          legend: { orientation: "h" },
          // shapes: [
          //   {
          //     type: "rect",
          //     xref: "x",
          //     yref: "paper",
          //     x0: -1,
          //     x1: -selectedVaR,
          //     y0: 0,
          //     y1: 1,
          //     fillcolor: "rgba(255, 0, 0, 0.1)",
          //     line: { width: 0 },
          //   },
          // ],
          shapes: [
            // VaR area (left of VaR)
            {
              type: "rect",
              xref: "x",
              yref: "paper",
              x0: Math.min(...data),
              x1: -selectedVaR,
              y0: 0,
              y1: 1,
              fillcolor: "rgba(255, 0, 0, 0.1)",
              line: { width: 0 },
            },
            // CVaR highlight - 20% portion of VaR area
            {
              type: "rect",
              xref: "x",
              yref: "y",  // Changed from "paper" to "y" to follow histogram height
              x0: -selectedVaR,
              x1: -selectedVaR + highlightPortion * (-cvar + selectedVaR),
              y0: 0,
              // y1: Math.max(...histogramYValues),  // Use actual max y of histogram
              y1: 1,  // Use actual max y of histogram
              fillcolor: "rgba(255, 165, 0, 0.3)",  // light orange
              line: { width: 0 }
            },
          ],
        }}
        style={{ width: "100%", height: "700px" }}
      />

  {/* Excel Export Icon */}
  <img
    src="https://img.icons8.com/color/48/microsoft-excel-2019--v1.png"
    alt="Export CSV"
    title="Export to CSV"
    style={{
      position: "absolute",
      top: "25px",
      right: "550px",
      width: "30px",
      height: "30px",
      cursor: "pointer",
      zIndex: 10
    }}
    onClick={handleExport}
  />

  {/* VaR Info Box */}
  <div
    style={{
      position: "absolute",
      top: "30px",
      right: "300px",
      background: "white",
      border: "1px solid #ccc",
      padding: "10px",
      borderRadius: "8px",
      fontSize: "14px",
      lineHeight: "1.4",
      boxShadow: "0 2px 6px rgba(0,0,0,0.1)",
      zIndex: 10,
    }}
  >
    <strong>📊 Risk Summary</strong><br />
    VaR ({days}d @ {confidence}%): <span style={{ color: "red" }}>
      {(-selectedVaR * 100).toFixed(2)}%
    </span><br />
    CVaR: <span style={{ color: "orange" }}>
      {(-cvar * 100).toFixed(2)}%
    </span><br />
    LGD: <span style={{ color: "gray" }}>
      {(-lossDefault * 100).toFixed(2)}%
    </span>
  </div>
</div>


      {/*<div style={{ marginTop: "20px" }}>*/}
      {/*  <p>*/}
      {/*    <strong>VaR (1-{days} day):</strong> ₹{(selectedVaR * 100).toFixed(2)}%*/}
      {/*  </p>*/}
      {/*  <p>*/}
      {/*    <strong>CVaR:</strong> ₹{(cvar * 100).toFixed(2)}%*/}
      {/*  </p>*/}
      {/*  <p>*/}
      {/*    <strong>Loss Given Default (LGD):</strong> ₹{(lossDefault * 100).toFixed(2)}%*/}
      {/*  </p>*/}
      {/*</div>*/}
    </div>
  );
};

export default VaRVisualizerDetailed;
